import * as React from 'react'

import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { CalendricalType } from '~/client/graph'
import { Skeleton } from '~/client/src/shared/components/shadcn-ui/skeleton'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import RecurringWorkflowSettings from '~/client/src/shared/models/RecurringWorkflowSettings'
import RecurringWorkflowSettingsStore from '~/client/src/shared/stores/domain/RecurringWorkflowSettings.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore, {
  isAfter,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import { recurringTypes } from '~/client/src/shared/utils/CalendricalTypeUtils'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import StruxhubInputHelperText from '../../../StruxhubInputs/components/StruxhubInputHelperText'
import StruxhubInput from '../../../StruxhubInputs/StruxhubInput'
import StruxhubSelect from '../../../StruxhubInputs/StruxhubSelect'
import StruxhubTextValueSelector from '../../../StruxhubInputs/StruxhubSelector/StruxhubTextValueSelector'
import WeekdaysSelector from '../../../WeekdaysSelector/WeekdaysSelector'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'

interface IProps {
  sitePermitCreationFormStore: SitePermitCreationFormStore

  recurringWorkflowSettingsStore?: RecurringWorkflowSettingsStore
  projectDateStore?: ProjectDateStore
  state?: InitialState
}

@inject('recurringWorkflowSettingsStore', 'state', 'projectDateStore')
@observer
export default class RecurringWorkflowOptions extends React.Component<IProps> {
  @observable private localProps: IProps = null
  @observable private isToggled: boolean = false
  private recurringSettingsIdDisposer?: () => void

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    runInAction(() => {
      this.isToggled =
        !!props.sitePermitCreationFormStore.editablePermit.recurringSettingsId
    })

    makeObservable(this)
  }

  public componentDidMount() {
    const { sitePermitCreationFormStore, recurringWorkflowSettingsStore } =
      this.props
    const { editablePermit } = sitePermitCreationFormStore

    if (editablePermit.recurringSettingsId) {
      recurringWorkflowSettingsStore.loadRecurringSetting(
        editablePermit.recurringSettingsId,
      )
    }

    recurringWorkflowSettingsStore.subscribeToSettings()

    this.recurringSettingsIdDisposer = reaction(
      () => editablePermit.recurringSettingsId,
      recurringSettingsId => {
        if (!recurringSettingsId && this.isToggled) {
          runInAction(() => {
            this.isToggled = false
          })
        }
      },
      { fireImmediately: false },
    )
  }

  public componentWillUnmount() {
    this.recurringSettingsIdDisposer?.()
    this.props.recurringWorkflowSettingsStore.clearData()
    this.props.recurringWorkflowSettingsStore.unsubscribeFromSettings()
  }

  public render() {
    const {
      editableRecurringSettings,
      isRecurringSettingsChanged,
      isRecurringSettingsValid,
      isRecurringSettingsDateValid,
    } = this.localProps.sitePermitCreationFormStore

    return (
      <>
        <div
          className={classList({
            'col my8 pa10 brada8 text large': true,
            'ba-dashed-palette-grey': isRecurringSettingsValid,
            'ba-dashed-palette-red': !isRecurringSettingsValid,
          })}
        >
          <div className="tw:flex tw:items-center my5">
            <span className="tw:flex-1">
              {Localization.translator.repeatWorkflow}
            </span>
            <Switch
              checked={this.isToggled}
              onCheckedChange={this.onRepeatToggle}
              disabled={!this.canEdit}
            />
          </div>
          {this.isToggled && (
            <div className="col">
              <div className="mb4 row x-between y-center no-outline-container">
                <span className="mr5 text large capitalize">
                  {Localization.translator.every_plural}
                </span>
                <div className="row align-start gap-8">
                  {editableRecurringSettings ? (
                    <>
                      <StruxhubInput
                        type="number"
                        min={1}
                        minLength={1}
                        value={
                          editableRecurringSettings.frequency?.toString() ||
                          EMPTY_STRING
                        }
                        onChange={this.changeFrequency}
                        className="half-flex-basis"
                        isDisabled={!this.canEdit}
                        isMinimalisticMode
                        noRightPadding
                      />
                      <StruxhubSelect
                        isMinimalisticMode={true}
                        value={editableRecurringSettings.frequencyType}
                        onChange={this.changeFrequencyType}
                        className="full-flex-basis"
                        isDisabled={!this.canEdit}
                      >
                        {recurringTypes.map(frequencyType => (
                          <option key={frequencyType} value={frequencyType}>
                            {this.getFrequencyTypeLabel(frequencyType)}
                          </option>
                        ))}
                      </StruxhubSelect>
                    </>
                  ) : (
                    <>
                      <Skeleton className="half-flex-basis tw:mt-2 tw:mb-1 tw:h-10" />
                      <Skeleton className="full-flex-basis tw:mt-2 tw:mb-1 tw:h-10" />
                    </>
                  )}
                </div>
              </div>
              {editableRecurringSettings?.frequencyType ===
                CalendricalType.Week && (
                <div className="row y-center no-outline-container">
                  <span>{Localization.translator.repeatOn}</span>
                  <WeekdaysSelector
                    className="py10"
                    selectedDayNumbers={
                      editableRecurringSettings?.selectedDays || []
                    }
                    onClick={this.changeDayToRepeat}
                  />
                </div>
              )}
              <div className="mb4 row y-center no-outline-container">
                <span className="mr5">{Localization.translator.endsOn}</span>
                {editableRecurringSettings ? (
                  <StruxhubTextValueSelector
                    value={this.endDateValue}
                    isValid={isRecurringSettingsDateValid}
                    isDisabled={!this.canEdit}
                    onClick={this.showDatePicker}
                    isMinimalisticMode={true}
                  />
                ) : (
                  <Skeleton className="tw:mt-2 tw:mb-1 tw:h-10" />
                )}
              </div>
            </div>
          )}
        </div>
        <StruxhubInputHelperText
          isChanged={isRecurringSettingsChanged}
          isValid={isRecurringSettingsValid}
          validationMessage={Localization.translator.invalidSettings}
        />
      </>
    )
  }

  private get canEdit(): boolean {
    const { existingPermit } = this.localProps.sitePermitCreationFormStore
    const { startOfDay } = this.localProps.projectDateStore

    return (
      !existingPermit ||
      existingPermit.isDraft ||
      isAfter(existingPermit.startDate, startOfDay(new Date()))
    )
  }

  @action.bound
  private onRepeatToggle() {
    const { sitePermitCreationFormStore, projectDateStore } = this.localProps
    const { editablePermit, existingPermit, maxRecurringDuration } =
      sitePermitCreationFormStore

    this.isToggled = !this.isToggled

    if (!this.isToggled) {
      sitePermitCreationFormStore.setRecurringSettings(null)
      if (existingPermit?.id && existingPermit.isDraft) {
        editablePermit.recurringSettingsId = null
      }
      return
    }

    sitePermitCreationFormStore.setRecurringSettings(
      RecurringWorkflowSettings.createNewWeeklySetting(
        editablePermit.projectId,
        projectDateStore,
        [Number(projectDateStore.getWeekdayNumber(editablePermit.startDate))],
        projectDateStore
          .addDays(editablePermit.startDate || new Date(), maxRecurringDuration)
          .getTime(),
      ),
    )
  }

  @action
  private changeFrequency = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { sitePermitCreationFormStore } = this.localProps
    const { editableRecurringSettings } = sitePermitCreationFormStore
    const settings =
      editableRecurringSettings.getCopy() as RecurringWorkflowSettings

    settings.frequency = Math.max(event.target.valueAsNumber, 1) || 1
    sitePermitCreationFormStore.setRecurringSettings(settings)
  }

  @action
  private changeFrequencyType = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { sitePermitCreationFormStore } = this.localProps
    const { editableRecurringSettings } = sitePermitCreationFormStore

    const newFrequencyType = event.target.value as CalendricalType
    if (!newFrequencyType || newFrequencyType === CalendricalType.Month) {
      return
    }

    const settings =
      editableRecurringSettings.getCopy() as RecurringWorkflowSettings
    settings.frequencyType = newFrequencyType
    sitePermitCreationFormStore.setRecurringSettings(settings)
  }

  @action
  private changeDayToRepeat = (dayNumber: number) => {
    if (!this.canEdit) {
      return
    }

    const {
      sitePermitCreationFormStore,
      projectDateStore: { workingDaysMap },
    } = this.localProps
    const { editableRecurringSettings } = sitePermitCreationFormStore

    const {
      state: {
        userActiveProjectSettings: { isAdmin },
      },
    } = this.localProps

    if (!isAdmin && !workingDaysMap[dayNumber]) {
      return
    }

    const settings =
      editableRecurringSettings.getCopy() as RecurringWorkflowSettings

    if (!settings.selectedDays) {
      settings.selectedDays = []
    }

    const index = settings.selectedDays.findIndex(day => day === dayNumber)
    if (index === -1) {
      settings.selectedDays.push(dayNumber)
    } else {
      settings.selectedDays.splice(index, 1)
    }

    sitePermitCreationFormStore.setRecurringSettings(settings)
  }

  private getFrequencyTypeLabel = (frequencyType: CalendricalType): string => {
    const { editableRecurringSettings } =
      this.localProps.sitePermitCreationFormStore

    switch (frequencyType) {
      case CalendricalType.Day:
        return Localization.translator.xDays(
          editableRecurringSettings?.frequency,
        )
      case CalendricalType.Week:
        return Localization.translator.xWeeks(
          editableRecurringSettings?.frequency,
        )
    }
  }

  @computed
  private get endDateValue(): string {
    const {
      sitePermitCreationFormStore: { editableRecurringSettings },
      projectDateStore: { getMonthDayAndYearToDisplay, endOfDay },
    } = this.localProps

    const endDay = endOfDay(editableRecurringSettings?.endDate || Date.now())
    return getMonthDayAndYearToDisplay(endDay)
  }

  @action
  private showDatePicker = () => {
    if (!this.canEdit) return

    this.localProps.sitePermitCreationFormStore.isRecurringDatepickerShown = true
  }
}
